/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *
 */

import { Note } from '@models/note/note';
import { Search } from '@models/cooking/search';
import { Contact } from '@models/contact';
import { User, type AdminUserSummary } from '@models/user/user';
import { Credential } from '@models/user/credential';
import I18N from '@utils/i18n.base';
import type { SearchHit, SearchScope } from '@components/search/types';
import { noteToHit } from '@pages/note/search-helpers';
import { cookingResultToHit } from '@pages/cooking/search-helpers';

const enc = encodeURIComponent;

function includesQuery(q: string, ...fields: (string | undefined)[]): boolean {
    const needle = q.toLowerCase();
    return fields.some((f) => (f ?? '').toLowerCase().includes(needle));
}

function contactName(c: Contact): string {
    const full = [c.firstName, c.familyName, c.lastName]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(' ');
    return full || c.nickName || c.altName || c.description || I18N.app.unknown;
}

// Lightweight session caches for scopes without a server search endpoint.
// Failed fetches are not cached so the next keystroke can retry.
let usersCache: Promise<AdminUserSummary[]> | null = null;
let credentialsCache: Promise<Credential[]> | null = null;

function getUsers(): Promise<AdminUserSummary[]> {
    return (usersCache ??= User.adminList().catch((e) => {
        usersCache = null;
        throw e;
    }));
}

function getCredentials(): Promise<Credential[]> {
    return (credentialsCache ??= Credential.list().catch((e) => {
        credentialsCache = null;
        throw e;
    }));
}

/**
 * Scope registry consumed by the header's GlobalSearch. Admin-only scopes
 * (users, credentials) are included only for SuperAdmins, matching the nav.
 */
export function buildSearchScopes(isSU: boolean): SearchScope[] {
    const scopes: SearchScope[] = [
        {
            key: 'notes',
            label: I18N.note.title,
            placeholder: I18N.note.searchNotesPlaceholder,
            live: true,
            resultsPath: (q) => `/note/search?q=${enc(q)}`,
            search: async (q) => (await Note.search(q, { limit: 8 })).map(noteToHit),
        },
        {
            key: 'cooking',
            label: I18N.cooking.cooking,
            placeholder: `${I18N.app.search} ${I18N.cooking.cooking}...`,
            live: true,
            resultsPath: (q) => `/cooking/search?q=${enc(q)}`,
            search: async (q) => (await Search.unifiedSearch(q)).slice(0, 8).map(cookingResultToHit),
        },
        {
            key: 'contacts',
            label: I18N.app.contacts,
            placeholder: `${I18N.app.search} ${I18N.app.contacts}...`,
            live: true,
            resultsPath: (q) => `/contact?q=${enc(q)}`,
            search: async (q) =>
                (await Contact.list({ search: q })).slice(0, 8).map(
                    (c): SearchHit => ({
                        id: c.id,
                        title: contactName(c),
                        subtitle: c.description || c.nickName || c.altName || undefined,
                        icon: 'person',
                        to: `/contact?id=${enc(c.id)}`,
                    })
                ),
        },
    ];

    if (isSU) {
        scopes.push(
            {
                key: 'credentials',
                label: I18N.app.credentials,
                placeholder: `${I18N.app.search} ${I18N.app.credentials}...`,
                live: true,
                resultsPath: (q) => `/user/credentials?q=${enc(q)}`,
                search: async (q) =>
                    (await getCredentials())
                        .filter((c) => includesQuery(q, c.name))
                        .slice(0, 8)
                        .map(
                            (c): SearchHit => ({
                                id: c.id,
                                title: c.name || I18N.app.unknown,
                                subtitle: undefined,
                                icon: 'key',
                                to: `/user/credentials?id=${enc(c.id)}`,
                            })
                        ),
            },
            {
                key: 'users',
                label: I18N.admin.user,
                placeholder: `${I18N.app.search} ${I18N.admin.user}...`,
                live: true,
                resultsPath: () => '/admin/users',
                search: async (q) =>
                    (await getUsers())
                        .filter((u) => includesQuery(q, u.name, u.email))
                        .slice(0, 8)
                        .map(
                            (u): SearchHit => ({
                                id: u.id,
                                title: u.name || u.email,
                                subtitle: u.email,
                                icon: 'manage_accounts',
                                to: `/user/${enc(u.id)}`,
                            })
                        ),
            }
        );
    }

    return scopes;
}
